<template>
  <div class="smart-player-timeline"
       @touchstart="onTouchStart"
       @touchmove="onTouchMove"
       @touchend="onTouchEnd"
  >
    <CamsButton
      class="smart-player-timeline__arrow"
      icon-type="only"
      type="button"
      @click="setFollowTimeShift(false); saveSeekBarMoving(seekBarOffsetX + 15)"
    >
      <svg class="player-icon player-icon_light">
        <use xlink:href="./img/icons.svg#icon-arrow-left" />
      </svg>
    </CamsButton>

    <div class="smart-player-timeline__seek-bar seek-bar">
      <div
        ref="seekBar"
        :style="{ transform: `translateX(${seekBarOffsetX}px)`, width: `${seekBarWidth}px` }"
        class="seek-bar__infinity"
        @dblclick="seek"
        @mousedown="isMovingTrigger({isMovingTimeline: true})"
        @mouseleave="hideTooltip()"
        @mousemove="showTooltip"
        @mouseup="seekViaClick"
        @wheel.prevent="zoomSeekBar"
      >
        <div class="seek-bar__lines">
          <div
            v-for="(lineFragmentStyle, lineFragmentIndex) in lineFragmentsStyles"
            :key="`lf-${lineFragmentIndex}`"
            :style="lineFragmentStyle"
            class="line-fragment"
          />

          <div v-if="!blockLiveMode" :style="{ transform: `translateX(${seekBarWidth-30}px)`, width: `30px`}" class="live-fragment" />
        </div>

        <div class="seek-bar__times">
          <div
            v-for="(markValue, markIndex) in timeMarks"
            :key="markIndex"
            :style="{transform: `translateX(${markValue * markIndent}px)`}"
            class="time-mark"
          >
            <span v-if="markValue % 6 === 0" class="time-mark__signed">
              {{ (unixLimitFrom + markValue * secondsPerInterval) * 1000 | msBySize(size) }}
            </span>
          </div>
        </div>

        <div v-show="tooltipOffsetX > 0" :style="{transform: `translateX(${tooltipOffsetX}px)`}" class="seek-bar__tooltip" />

        <div v-if="downloadMode" class="seek-bar__download-border">
          <div class="download-border" :class="{'download-border_warn': showWarnDownloadBorders}">
            <div
              :style="{transform: `translateX(${ downloadFromOffsetX }px)`, width: `${downloadToOffsetX - downloadFromOffsetX}px`}"
              class="download-border__between"
              @mousedown.stop="isMovingTrigger({isMovingDownloadDelta: true})"
            />
            <div
              :style="{transform: `translateX(${ downloadFromOffsetX }px)`}"
              class="download-border__border"
              @mousedown.stop="isMovingTrigger({isMovingDownloadFrom: true})"
            >
              <div class="border-points">
                <span />
                <span />
                <span />
              </div>
            </div>
            <div
              :style="{transform: `translateX(${ downloadToOffsetX }px)`}"
              class="download-border__border"
              @mousedown.stop="isMovingTrigger({isMovingDownloadTo: true})"
            >
              <div class="border-points">
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        </div>

        <div
          :style="{transform: `translateX(${ timeShiftMarkOffsetX }px)`}"
          class="seek-bar__time-shift-mark"
          @mousedown.stop="isMovingTrigger({isMovingTimeShift: true})"
        />
      </div>
    </div>

    <CamsButton
      class="smart-player-timeline__arrow"
      icon-type="only"
      type="button"
      @click="setFollowTimeShift(false); saveSeekBarMoving(seekBarOffsetX - 15)"
    >
      <svg class="player-icon player-icon_light">
        <use xlink:href="./img/icons.svg#icon-arrow-right" />
      </svg>
    </CamsButton>

    <div
      v-show="tooltipOffsetX > 0 && componentWithContent"
      :style="{transform: `translate(${componentWithContentOffsetX}px, -195px)`}"
      class="smart-player-timeline__custom-component"
    >
      <component :is="componentWithContent" v-if="componentWithContent" />
    </div>
    <div
      v-show="tooltipOffsetX > 0"
      :style="{transform: `translate(${timeCaptionOffsetX}px, -43px)`}"
      class="smart-player-timeline__time-caption"
    >
      {{ (unixLimitFrom + tooltipOffsetX * secondsPerPixel) * 1000 | localDateTime }}
    </div>
  </div>
</template>

<script>
import {TIMELINE_SIZES} from "./mixins.js";

/**
 * Компонент для работы со временной шкалой для плеера, который поддерживает работу в режиме архива.
 * Временная шкала "бесконечная" и служит инструментом для перемещения во времени.
 * Границы доступных временных рамок для перемещения определяет родительский компонент,
 * передавая в параметрах границы доступного в архиве видео.
 *
 * Для внесения изменений в механизм перемещения по шкале, а так же способы отрисовки элементов шкалы (засечки и цветные полоски)
 * необходимо учесть работу CSS свойств `transform` и функции `translateX`,
 * и принять во внимание вопросы позиционирования элементов в документе.
 *
 * Механизм перемещения по шкале включается при зажатии ЛКМ на самой шкале или на ее элементах.
 * Для отслеживания перемещения при зажатой ЛКМ и отпускание ЛКМ регистрируются глобальные события в window:
 *  - mousemove - Перемещение элементов шкалы следует корректно обрабатывать вне зависимости от того,
 *  где перемещается мышь - в пределах браузера или уже за его пределами, при условии все еще зажатой ЛКМ.
 *  Постоянно отслеживать перемещение не нужно, обработчик регистрируется в момент **включения** перемещения,
 *  а его удаление происходит при отпускании ЛКМ, т.е. при явной остановке перемещения элементов.
 *  - mouseup - При перемещении шкалы и ее элементов во время зажатой ЛКМ нужно следить за курсором
 *  и отключать отслеживание во время отпускания ЛКМ.
 *  Регистрация обработчика при запуске компонента и удалять при его уничтожении.
 *
 * Помимо зажатия ЛКМ на шкале, отслеживаются однократные нажатия по ней для перехода к нужному участку архива.
 * Механизм точечного отслеживания кликов ЛКМ наряду с отслеживанием зажатой ЛКМ заимствован из SmartPlayer и детально описан там.
 *
 */
export default {
  name: "SmartPlayerTimeline",
  filters: {
    /**
     * @param {Number} ms Миллисекунды
     * @param {Number} size Размер шкалы
     * @return {String} Строковое представление указанных миллисекунд для соответствующего размера шкалы. Вернет дату или время.
     */
    msBySize(ms, size) {
      let format = "HH:mm:ss";
      if (size >= 4) {
        format = "DD.MM HH:mm";
      }
      if (size >= 8) {
        format = "DD.MM ";
      }
      return moment(ms).format(format);
    }
  },
  props: {
    /**
     * Левый лимит временной шкалы, меньше которого архив не доступен.
     */
    availableArchiveFrom: {
      type: Date,
      required: true,
    },
    /**
     * Правый лимит временной шкалы, после которого архив не доступен.
     */
    availableArchiveTo: {
      type: Date,
      required: true,
    },
    /**
     * Значение для индикации текущего прогресса воспроизведения на шкале.
     */
    timeShift: {
      type: Date,
      required: true,
    },
    /**
     * Начальное значение для флага для отображения временной шкалы в области видимости текущего прогресса.
     * Изменение статуса флага происходит по прямому вызову метода `setFollowTimeShift` в этом компоненте.
     */
    initialFollowTimeShift: {
      type: Boolean,
      default: false,
    },
    /**
     * Функция вернет массив из объектов c данными для рисования отрезков линий на временной шкале.
     *
     * Поскольку временная шкала "длинная" (бесконечная), а просматривается всегда только ограниченный ее участок,
     * то рисовать все элементы на ней избыточно.
     * Эта функция будет принимать 2 метки времени - границы просматриваемой шкалы, и функция должна вернуть те данные, которые актуальны для
     * просмотра в данном временном диапазоне, т.е. вызывать ее надо каждый раз когда меняется просматриваемая область.
     *
     * Структура которую должна вернуть функция:
     *
     * ```
     * [
     *   [
     *    дата начала,
     *    дата завершения,
     *    строка с цветом для линии,
     *    число - приоритет (с высоким приоритетом линии закрывают собой линии с низким)
     *   ],
     *   ...
     * ]
     * ```
     */
    getLineFragments: {
      type: Function,
      /**
       * @param {Date} leftBoundary Левая граница отображаемой на шкале области.
       * @param {Date} rightBoundary Правая граница отображаемой на шкале области.
       * @return {Promise<Array<Array<Date, Date, String, Number>>>}
       */
      // eslint-disable-next-line no-unused-vars
      async default(leftBoundary, rightBoundary) {
        return [];
      },
    },
    /**
     * Начальное значение номера размера масштаба для временной шкалы.
     */
    initialSize: {
      type: Number,
      default: 0,
      validator(size) {
        return size in TIMELINE_SIZES;
      },
    },
    /**
     * Флаг для принудительной блокировки функций перехода к прямой трансляции.
     * Применять когда нужен плеер только для просмотра архива даже если прямая трансляция доступна.
     */
    blockLiveMode: {
      type: Boolean,
      default: false,
    },
    /**
     * Флаг для включения опций режима скачивания.
     */
    downloadMode: {
      type: Boolean,
      default: false,
    },
    /**
     * Переданная дата начальной границы для скачивания архива.
     */
    initialDownloadFrom: {
      type: Date,
      default: null,
    },
    /**
     * Переданная дата конечной границы для скачивания архива.
     */
    initialDownloadTo: {
      type: Date,
      default: null,
    },
    /**
     * Минимальное количество секунд для скачивания. Определяет границы скачивания на шкале и в календаре. По умолчанию 20 сек.
     */
    minUnixDownloadDelta: {
      type: Number,
      default: 20,
    },
    /**
     * Максимальное количество секунд для скачивания. Определяет границы скачивания на шкале и в календаре. По умолчанию 8 часов.
     */
    maxUnixDownloadDelta: {
      type: Number,
      default: 28800,
    },
    /**
     * Функция которая должна принять метку времени и вернуть компонент с контентом для данного времени,
     * чтобы его можно было отобразить рядом со шкалой.
     *
     * Компонент будет размещаться над шкалой в момент наведения на нее мышки (и определения времени над шкалой).
     * Визуально компонент ограничен размерами: width: 320px; height: 270px;
     */
    getComponentWithContent: {
      type: Function,
      // eslint-disable-next-line no-unused-vars
      async default(interestingDate) {
        return null;
      },
    },
  },
  data() {
    return {
      touchStartX: null, // Начальная координата касания
      // Флаги для контроля ручного перемещения элементов при зажатой кнопке мыши.
      isMovingTimeline: false, // Перемещение временной шкалы.
      isMovingTimeShift: false, // Перемещение timeShift.
      isMovingDownloadFrom: false, // Перемещение левой границы для скачивания.
      isMovingDownloadTo: false, // Перемещение правой границы для скачивания.
      isMovingDownloadDelta: false, // Перемещение правой и левой границы для скачивания с сохранением расстояния между ними.
      startMovingPageX: null, // Зафиксированное значение pageX мыши на момент начала перемещения.
      startMovingOffsetX: null, // Зафиксированное смещение timeline по X на момент начала перемещения.
      startDownloadPositionXDelta: null, // Зафиксированное значение мыши на дельте на момент начала перемещения дельты загрузки.
      startDownloadOffsetsDelta: null, // Зафиксированная разница между смещениями на момент начала перемещения дельты загрузки.
      // Значения по оси X/Y для смещений по временной шкале через translate/X.
      seekBarOffsetX: 0, // Фон бесконечной временной шкалы.
      timeShiftMarkOffsetX: 0, // Индикатор прогресса.
      downloadFromOffsetX: 0, // Левая граница для скачивания.
      tooltipOffsetX: 0, // Индикатор перемещения мыши на временной шкале.
      timeCaptionOffsetX: 0, // Подсказка со временем для мыши на временной шкале.
      componentWithContentOffsetX: 0, // Область со скриншотом.
      componentWithContent: null, // Компонент с любым контентом. Как правило связан с событиями или скриншотами.
      downloadToOffsetX: 0, // Правая граница для скачивания.
      downloadFrom: this.initialDownloadFrom, // Конечная граница для скачивания архива.
      downloadTo: this.initialDownloadTo, // Начальная граница для скачивания архива.
      showWarnDownloadBorders: false, // Предупреждение о запредельных границах скачивания архива.
      timeMarks: [], // Позиции для засечек на временной шкале.
      followTimeShift: this.initialFollowTimeShift, // Флаг для динамического следования временной шкалы вслед за текущим прогрессом.
      size: this.initialSize, // Номер размера масштаба для временной шкалы.
      singleClickOnSeekBar: false, // Флаг для точного отслеживания ЛКМ по шкале для рысканья.
      timeoutIdForSingleClickOnSeekBar: null, // Таймер для автоматического снятия флага для точного отслеживания ЛКМ по шкале.
      lineFragmentsData: [], // Массив из объектов c данными для рисования отрезков линий на временной шкале.
      timeoutIdForUpdatingLineFragments: null, // Таймер для регулярного обновления данных для рисования отрезков.
      // Границы (Date) отображаемой пользователю шкалы с упреждением, для запроса информации, которая будет отрисована на шкале.
      advancedLeftBoundary: null, // Левая граница с упреждением, либо дата начала архива.
      advancedRightBoundary: null, // Правая граница с упреждением, либо дата конца архива + небольшое фиксированное упреждение.
    };
  },
  computed: {
    /**
     * @return {Number} Unix представление времени availableArchiveFrom в секундах.
     */
    unixAvailableArchiveFrom() {
      return Math.trunc(this.availableArchiveFrom / 1000);
    },
    /**
     * @return {Number} Unix представление времени availableArchiveTo в секундах.
     */
    unixAvailableArchiveTo() {
      return Math.trunc(this.availableArchiveTo / 1000);
    },
    /**
     * Unix представление времени в секундах наименьшей даты, от которой будет происходить отрисовка шкалы.
     * Расчет с даты начала доступного архива с запасом.
     *
     * @return {Number}
     */
    unixLimitFrom() {
      const limitFrom = new Date(this.availableArchiveFrom);
      limitFrom.setDate(this.availableArchiveFrom.getDate() - 30);
      limitFrom.setHours(0, 0, 0, 0);
      return Math.trunc(limitFrom / 1000);
    },
    /**
     * Количество секунд в одном пикселе на временной шкале.
     */
    secondsPerPixel() {
      return TIMELINE_SIZES[this.size][0];
    },
    /**
     * Размер отступа между засечками.
     */
    markIndent() {
      return TIMELINE_SIZES[this.size][1];
    },
    /**
     * @return {Number} Количество секунд между маркерами для разграничения интервалов на временной шкале.
     */
    secondsPerInterval() {
      return this.markIndent * this.secondsPerPixel;
    },
    /**
     * @return {Number} Расчетное значение в пикселях для ширины всей доступной временной шкалы.
     */
    seekBarWidth() {
      return Math.round((this.unixAvailableArchiveTo - this.unixLimitFrom) / this.secondsPerPixel) + (this.blockLiveMode ? 0 : 30);
    },
    /**
     * @return {Array<{"background-color": String, "width": String, "transform": String}>} Массивы со стилями для каждого отрезка.
     */
    lineFragmentsStyles() {
      if (!Array.isArray(this.lineFragmentsData)) {
        return [];
      }

      return this.lineFragmentsData.map(([fragmentStart, fragmentEnd, fragmentColor]) => {
        return {
          "background-color": fragmentColor,
          // Минимальная ширина длина линии будет 5px = стандартная длительность события 5c,
          // но поскольку на разных масштабах такого события видно не будет то ставим всегда минимум 5px чтобы его можно было различить на шкале.
          "width": `${Math.max((fragmentEnd - fragmentStart) / 1000 / this.secondsPerPixel, 5)}px`,
          "transform": `translateX(${((fragmentStart / 1000) - this.unixLimitFrom) / this.secondsPerPixel}px)`,
        };
      });
    },
    /**
     * @return {Number} Unix представление времени timeShift в секундах.
     */
    unixTimeShift() {
      return Math.trunc(this.timeShift / 1000);
    },
  },
  watch: {
    /**
     * Наблюдение за концом архива для обновления данных на шкале.
     */
    availableArchiveTo() {
      this.throttledUpdateLineFragments();
    },
    /**
     * Наблюдение за внешним значением размера шкалы для синхронного изменения с локальным.
     */
    initialSize(newSize) {
      this.saveSize(newSize);
    },
    /**
     * При необходимости следовать за текущим временным сдвигом - корректировать отображаемую область.
     */
    unixTimeShift() {
      this.moveTimeShift();
    },
    /**
     * При переключении режима скачивания нужно устанавливать по умолчанию границы для области скачивания.
     */
    downloadMode() {
      this.initDownloadMode();
    },
    /**
     * Наблюдение за оригинальными границами скачивания архива для синхронизации между компонентами.
     */
    initialDownloadFrom(downloadFrom) {
      if ((downloadFrom instanceof Date) && (this.downloadTo instanceof Date)) {
        if (Math.trunc(+downloadFrom / 1000) !== Math.trunc(+this.downloadFrom / 1000)) {
          this.saveDownloadBordersMoving(downloadFrom, this.downloadTo);
        }
      }
    },
    /**
     * Наблюдение за оригинальными границами скачивания архива для синхронизации между компонентами.
     */
    initialDownloadTo(downloadTo) {
      if ((this.downloadFrom instanceof Date) && (downloadTo instanceof Date)) {
        if (Math.trunc(+downloadTo / 1000) !== Math.trunc(+this.downloadTo / 1000)) {
          this.saveDownloadBordersMoving(this.downloadFrom, downloadTo);
        }
      }
    },
  },
  /**
   * Регистрация отложенных функций изменения timeShift и downloadBorders,
   * для случаев, когда их изменение вызвано перемещением с зажатой кнопкой мыши, чтобы не создавать массу событий.
   * Регистрация отложенных функций получения компонентов с дополнительной информацией для отображения над шкалой.
   */
  created() {
    this.debouncedSeek = _.debounce(this.seek, 150);
    this.debouncedReceiveComponentWithContent = _.debounce(async (interestingDate = null) => {
      this.componentWithContent = interestingDate ? await this.getComponentWithContent(interestingDate) : null;
    }, 100);
    this.debouncedEmitDownloadBordersChange = _.debounce((downloadBorders) => {
      /**
       * Отдает актуальную пару дат - границы фрагмента для скачивания, которые были изменены на шкале.
       * @event download-borders-change
       */
      this.$emit("download-borders-change", downloadBorders);
    }, 150);
    this.throttledUpdateLineFragments = _.throttle(this.updateLineFragments, 333);
  },
  /**
   * Начальный расчет для индикатора прогресса проигрывания и центровка шкалы на индикаторе.
   * Запрос данных для рисования линий.
   * Регистрация глобального слушателя для отпускания ЛКМ для отключения отслеживания перемещения элементов на временной шкале.
   */
  mounted() {
    this.timeShiftMarkOffsetX = Math.round((this.unixTimeShift - this.unixLimitFrom) / this.secondsPerPixel);
    this.moveSeekBarToTimeShift();
    this.initDownloadMode(); // todo баг при начальном включенном режиме если были переданы значения границ извне при инициализации
    this.timeoutIdForUpdatingLineFragments = setTimeout(this.throttledUpdateLineFragments, 5000);
    this.updateLineFragments();
    window.addEventListener("mouseup", this.isMovingTrigger);
  },
  /**
   * Удаление таймера для обновления данных для рисования линий.
   * Удаление глобального слушателя отпускания ЛКМ.
   */
  beforeDestroy() {
    clearInterval(this.timeoutIdForUpdatingLineFragments);
    window.removeEventListener("mouseup", this.isMovingTrigger);
  },
  methods: {
    /**
     * Перемещение таймлайна касанием, для мобильных версий
     */
    onTouchStart(event) {
      // Сохраняем начальные координаты касания
      this.touchStartX = event.touches[0].clientX;
      this.startMovingOffsetX = this.seekBarOffsetX; // Текущее смещение таймлайна
    },
    onTouchMove(event) {
      if (this.touchStartX === null) return;

      const touchCurrentX = event.touches[0].clientX;
      const deltaX = touchCurrentX - this.touchStartX;

      // Рассчитываем новое смещение таймлайна
      let newOffsetX = this.startMovingOffsetX + deltaX;

      // Ограничиваем смещение в пределах доступного диапазона
      const limitOffsetX = -(this.seekBarWidth - this.$refs.seekBar.parentElement.getBoundingClientRect().width);
      newOffsetX = Math.min(0, Math.max(newOffsetX, limitOffsetX));

      this.seekBarOffsetX = newOffsetX;

      // Обновляем данные для отображения
      this.rerenderSeekBar();
    },
    onTouchEnd() {
      this.touchStartX = null; // Сбрасываем начальные координаты касания

    },
    /**
     * Переключатель флагов движения.
     * Включение флага движения может быть только при всех выключенных и только одного.
     * Выключать можно все сразу. Вызов без параметров направлен на отключение всех флагов.
     * Отслеживание момента прекращения перемещения по шкале попутно необходимо для обнуления значений для расчетов перемещения.
     *
     * При включении флагов нужно глобально отслеживать перемещение по шкале, а при выключении прекращать отслеживание.
     */
    isMovingTrigger({isMovingTimeline = false, isMovingTimeShift = false, isMovingDownloadFrom = false, isMovingDownloadTo = false, isMovingDownloadDelta = false} = {}) {
      if (!this.isMovingTimeline && !this.isMovingTimeShift && !this.isMovingDownloadFrom && !this.isMovingDownloadTo && !this.isMovingDownloadDelta) {
        if (isMovingTimeline) {
          this.singleClickOnSeekBar = this.isMovingTimeline = true;
        } else if (isMovingTimeShift) {
          this.singleClickOnSeekBar = this.isMovingTimeShift = true;
        } else if (isMovingDownloadFrom) {
          this.isMovingDownloadFrom = true;
        } else if (isMovingDownloadTo) {
          this.isMovingDownloadTo = true;
        } else if (isMovingDownloadDelta) {
          this.singleClickOnSeekBar = this.isMovingDownloadDelta = true;
        }
      }

      if (!isMovingTimeline) {
        this.isMovingTimeline = false;
        this.startMovingPageX = this.startMovingOffsetX = null;
      }
      if (!isMovingTimeShift) {
        this.isMovingTimeShift = false;
      }
      if (!isMovingDownloadFrom) {
        this.isMovingDownloadFrom = false;
      }
      if (!isMovingDownloadTo) {
        this.isMovingDownloadTo = false;
      }
      if (!isMovingDownloadDelta) {
        this.isMovingDownloadDelta = false;
        this.startDownloadOffsetsDelta = this.startDownloadPositionXDelta = null;
      }

      if (isMovingTimeline || isMovingTimeShift || isMovingDownloadFrom || isMovingDownloadTo || isMovingDownloadDelta) {
        this.timeoutIdForSingleClickOnSeekBar = setTimeout(() => {
          this.singleClickOnSeekBar = false;
        }, 333);
        window.addEventListener("mousemove", this.onMouseMoveOnSeekBar);
      } else {
        window.removeEventListener("mousemove", this.onMouseMoveOnSeekBar);
      }
    },
    /**
     * Реакция на событие `mousemove` для работы различных перемещений, в зависимости от текущего режима.
     * Перемещение по шкале при работе с границами скачивания архива исключает событие клика ЛКМ по шкале для перехода к архиву.
     *
     * @param {MouseEvent} mouseEvent
     */
    onMouseMoveOnSeekBar(mouseEvent) {
      if (this.isMovingTimeShift) {
        this.moveTimeShift(mouseEvent);
      }
      if (this.isMovingTimeline) {
        this.moveSeekBar(mouseEvent);
      }
      if (this.isMovingDownloadFrom || this.isMovingDownloadTo || this.isMovingDownloadDelta) {
        this.singleClickOnSeekBar = false;
        this.moveDownloadBorders(mouseEvent);
      }
    },
    /**
     * Клик по шкале, при условии что мышь не была в движении с момента зажатия ЛКМ, вызывает процедуру рысканья.
     * Событие срабатывает при отпускании ЛКМ и отслеживается непосредственно на бесконечной шкале.
     *
     * @param {MouseEvent} mouseEvent
     */
    seekViaClick(mouseEvent) {
      if (this.singleClickOnSeekBar) {
        this.singleClickOnSeekBar = false;
        clearTimeout(this.timeoutIdForSingleClickOnSeekBar);
        this.setFollowTimeShift(false);
        this.seek(mouseEvent);
      }
    },
    /**
     * Процедура рысканья по временной шкале.
     * Необходимо рассчитать значение по оси X для мыши на бесконечной временной шкале и соответствующую unixtime метку для этого значения.
     *
     * @param {MouseEvent} mouseEvent
     */
    seek(mouseEvent) {
      const newMouseX = mouseEvent.pageX - this.$refs.seekBar.getBoundingClientRect().left,
            newUnixTimeShift = this.unixLimitFrom + (newMouseX * this.secondsPerPixel),
            newTimeShift = new Date(newUnixTimeShift * 1000);

      /**
       * Происходит при клике пользователя на временной шкале с целью отобразить нужный участок архива
       * и отдает дату запрошенного участка.
       * @event time-shift-change
       */
      this.$emit("time-shift-change", newTimeShift);
    },
    /**
     * Изменение индикатора timeShift, вызванное ручным переносом с зажатой кнопкой мыши, или при внешнем изменении показателя прогресса.
     *
     * @param {MouseEvent} mouseEvent
     */
    moveTimeShift(mouseEvent = null) {
      if (!this.isMovingTimeShift) {
        this.timeShiftMarkOffsetX = Math.round((this.unixTimeShift - this.unixLimitFrom) / this.secondsPerPixel);
        if (this.followTimeShift) {
          this.moveSeekBarToTimeShift();
        }
        return;
      }
      if (mouseEvent) {
        this.timeShiftMarkOffsetX = mouseEvent.pageX - this.$refs.seekBar.getBoundingClientRect().left;
        this.debouncedSeek(mouseEvent);
      }
    },
    /**
     * Перемещение мыши при зажатой клавиши для перемещения по временной шкале.
     *
     * Через изменение стиля transform происходит смещение элемента относительно центра этого элемента.
     * Результат расчета новых значений для смещения центра картинки будет применен если он попадает в допустимые пределы перемещения
     * в контейнере с учетом масштабирования. В противном случае возможны пустые области, на которых уже события не отслеживаются.
     *
     * @param {MouseEvent} mouseEvent
     */
    moveSeekBar(mouseEvent) {
      if (!this.isMovingTimeline) {
        return;
      }
      this.setFollowTimeShift(false);
      // Первое движение после включения возможности перемещения картинки устанавливает начальные значения (точку отсчета) для расчетов.
      if ((this.startMovingPageX === null) || (this.startMovingOffsetX === null)) {
        this.startMovingPageX = mouseEvent.pageX;
        this.startMovingOffsetX = this.seekBarOffsetX;
      }

      this.saveSeekBarMoving(this.startMovingOffsetX - (this.startMovingPageX - mouseEvent.pageX));
    },
    /**
     * Функция для перерисовки временной шкалы, чтобы не показывать неотрисованную часть за пределами шкалы.
     * Вызывать снаружи компонента шкалы, когда происходит переход в/из полноэкранного режима.
     */
    rerenderSeekBar() {
      this.saveSeekBarMoving(this.seekBarOffsetX);
    },
    /**
     * Перемещение отображения временной шкалы к индикатору прогресса, чтобы прогресс был размещен по центру шкалы.
     */
    moveSeekBarToTimeShift() {
      this.saveSeekBarMoving(
        -Math.round((this.unixTimeShift - this.unixLimitFrom) / this.secondsPerPixel)
          + this.$refs.seekBar.parentElement.getBoundingClientRect().width / 2,
      );
    },
    /**
     * Сохранение перемещения для отображения участка временной шкалы.
     * Сохранение (и дальнейшее применение) допускается при соблюдении ограничений области перемещения по шкале в допустимых пределах.
     *
     * Смещение по оси X нужно для отображения участка шкалы, который необходим пользователю.
     * На шкале имеются временные засечки для ориентации во времени, но отображать засечки по всей длине шкалы
     * очень накладно (больше 1000 уже заметны лаги, больше 100000 страница не завершит загрузку).
     * По имеющимся сведениям мы можем определить видимую область шкалы и рассчитать: количество засечек и с какой позиции
     * необходимо отображать их на временной шкале, чтобы не рендерить слева и справа невидимые.
     *
     * @param {Number} offsetX Рассчитанное смещение в px.
     */
    saveSeekBarMoving(offsetX) {
      const containerSeekBarWidth = this.$refs.seekBar.parentElement.getBoundingClientRect().width,
            limitOffsetX = -(this.seekBarWidth - containerSeekBarWidth);

      offsetX = offsetX < limitOffsetX ? limitOffsetX : offsetX;
      offsetX = offsetX >= 0 ? 0 : offsetX;

      this.seekBarOffsetX = offsetX;
      this.throttledUpdateLineFragments();

      // Заполнение массива с позициями засечек с учетом текущего смещения по шкале.
      const visibleMarksCount = Math.trunc(containerSeekBarWidth / this.markIndent), // Количество видимых засечек.
            invisibleMarksCount = Math.trunc(Math.abs(offsetX) / this.markIndent); // Количество скрытых (слева) засечек = начало для расчета видимых.
      // При изменении обрати внимание на то как появляются и исчезают засечки при медленном перемещении по шкале (делай запас).
      // +1 к позиции для рендера, чтобы засечки исчезали сразу за скрытием слева (+0 плавно, но 1 засечка будет видна на половину)
      // +1 к количеству засечек, чтобы справа плавно появлялись.
      this.timeMarks = Array.from(
        {length: visibleMarksCount + 1},
        (markValue, markIndex) => invisibleMarksCount + markIndex + 1,
      );
    },
    /**
     * Обновление данных по фрагментам линий для рисования на шкале.
     *
     * Определяются временные границы, которые передаются в переданную извне функцию для получения исходных данных по линиям.
     * Сортируются в соответствии с приоритетами.
     * Запрос данных производится с упреждением времени, чтобы не запрашивать данные в тех границах шкалы, что были запрошены ранее.
     * Упреждение по границам +/-10% от видимой части архива, но не выходя за рамки доступного архива (исключение для правой границы +5 сек.).
     */
    async updateLineFragments() {
      if (!this.$refs.seekBar) {
        return;
      }
      // Расчет настоящих границ.
      // Границы шкалы по бокам могут содержать в себе запас упреждения, поэтому необходимо проверить их по наличию архива.
      let unixLeftBoundary = this.unixLimitFrom + Math.abs(this.seekBarOffsetX) * this.secondsPerPixel,
          unixRightBoundary = this.unixLimitFrom + (Math.abs(this.seekBarOffsetX) + this.$refs.seekBar.parentElement.getBoundingClientRect().width) * this.secondsPerPixel;
      if (unixLeftBoundary < this.unixAvailableArchiveFrom) {
        unixLeftBoundary = this.unixAvailableArchiveFrom;
      }
      if (unixRightBoundary > this.unixAvailableArchiveTo) {
        unixRightBoundary = this.unixAvailableArchiveTo;
      }
      const unixBoundaryDelta = unixRightBoundary - unixLeftBoundary, // Дельта между видимыми границами.
            unixBoundaryAdvance = Math.trunc(unixBoundaryDelta * 0.1), // Упреждение = 10% от дельты.
            realLeftBoundary = new Date(unixLeftBoundary * 1000),
            realRightBoundary = new Date(unixRightBoundary * 1000);

      // Если новые реальные границы укладываются в ранее сохраненные границы с упреждением (т.е. имеются данные по наблюдаемому участку шкалы),
      // то обновление данных по фрагментам линий не требуется.
      if ((+this.advancedLeftBoundary <= +realLeftBoundary) && (+this.advancedRightBoundary > +realRightBoundary)) {
        return;
      }

      // Расчет новых границ с упреждением.
      // Левая граница с упреждением не может быть меньше чем начало архива.
      let advancedLeftBoundary = this.unixAvailableArchiveFrom > (unixLeftBoundary - unixBoundaryAdvance)
            ? this.availableArchiveFrom
            : new Date((unixLeftBoundary - unixBoundaryAdvance) * 1000),
          // Если правая граница с упреждением больше чем конец архива, то граница определяется по концу архива со строгим упреждением в 5 сек.
          advancedRightBoundary = this.unixAvailableArchiveTo < (unixRightBoundary + unixBoundaryAdvance)
            ? new Date(+this.availableArchiveTo + 5000)
            : new Date((unixRightBoundary + unixBoundaryAdvance) * 1000);
      this.advancedLeftBoundary = advancedLeftBoundary;
      this.advancedRightBoundary = advancedRightBoundary;

      /** @type {Array<Array<Date, Date, String, Number>>} */
      const lineFragmentsData = await this.getLineFragments(advancedLeftBoundary, advancedRightBoundary);
      // В определении функции выше оговорен порядок параметров для линий которые должны быть возвращены функцией.
      // Функция сортировки стандартная для Array.
      lineFragmentsData.sort((a, b) => a[3] < b[3] ? -1 : (a[3] > b[3] ? 1 : 0));
      this.lineFragmentsData = lineFragmentsData;
    },
    /**
     * Отображение подсказок и скриншота.
     * Необходимо отображать подсказку именно рядом с позицией мыши на временной шкале.
     *
     * Для расчета по оси X нужно учитывать актуальное смещение 0 на X для перетаскиваемых элементов (в т.ч. в полноэкранном режиме).
     * Внимательно смотри на css для компонента.
     *
     * @param {MouseEvent} mouseEvent
     */
    showTooltip(mouseEvent) {
      const newMouseTooltipOffsetX = mouseEvent.pageX - this.$refs.seekBar.getBoundingClientRect().left,
            unixInterestingDate = this.unixLimitFrom + (newMouseTooltipOffsetX * this.secondsPerPixel),
            interestingDate = new Date(unixInterestingDate * 1000),
            newMouseTimelineOffsetX = mouseEvent.pageX - this.$el.getBoundingClientRect().left,
            timelineWidth = this.$el.getBoundingClientRect().width;
      this.tooltipOffsetX = newMouseTooltipOffsetX;
      this.debouncedReceiveComponentWithContent(interestingDate);
      // См. фиксированную ширину блока = 320, половина = 160.
      this.componentWithContentOffsetX = newMouseTimelineOffsetX - 160;
      this.componentWithContentOffsetX = Math.max(this.componentWithContentOffsetX, 0);
      this.componentWithContentOffsetX = Math.min(this.componentWithContentOffsetX, timelineWidth - 320);
      // См. фиксированную ширину блока = 128, половина = 64.
      this.timeCaptionOffsetX = newMouseTimelineOffsetX - 64;
      this.timeCaptionOffsetX = Math.max(this.timeCaptionOffsetX, 0);
      this.timeCaptionOffsetX = Math.min(this.timeCaptionOffsetX, timelineWidth - 128);
    },
    /**
     * Сокрытие подсказки на временной шкале простым сдвигом в недосягаемую область.
     */
    hideTooltip() {
      this.tooltipOffsetX = 0;
      this.debouncedReceiveComponentWithContent();
    },
    /**
     * Установка по умолчанию границ для области скачивания (+-5 мин в мс).
     */
    initDownloadMode() {
      this.saveDownloadBordersMoving(
        new Date(+this.timeShift - 300000),
        new Date(+this.timeShift + 300000),
      );
    },
    /**
     * Перемещение границ области скачивания.
     *
     * @param {MouseEvent} mouseEvent
     */
    moveDownloadBorders(mouseEvent) {
      let downloadFromOffsetX = this.downloadFromOffsetX,
          downloadToOffsetX = this.downloadToOffsetX;
      if (this.isMovingDownloadFrom) {
        downloadFromOffsetX = mouseEvent.pageX - this.$refs.seekBar.getBoundingClientRect().left;
      }
      if (this.isMovingDownloadTo) {
        downloadToOffsetX = mouseEvent.pageX - this.$refs.seekBar.getBoundingClientRect().left;
      }
      if (this.isMovingDownloadDelta) {
        // Первое движение после включения возможности перемещения картинки устанавливает начальные значения (точку отсчета) для расчетов.
        if ((this.startDownloadOffsetsDelta === null) || (this.startDownloadPositionXDelta === null)) {
          this.startDownloadPositionXDelta = downloadFromOffsetX - (mouseEvent.pageX - this.$refs.seekBar.getBoundingClientRect().left);
          this.startDownloadOffsetsDelta = this.downloadToOffsetX - this.downloadFromOffsetX;
        }
        downloadFromOffsetX = mouseEvent.pageX - this.$refs.seekBar.getBoundingClientRect().left + this.startDownloadPositionXDelta;
        downloadToOffsetX = downloadFromOffsetX + this.startDownloadOffsetsDelta;
      }

      this.saveDownloadBordersMoving(
        new Date((this.unixLimitFrom + downloadFromOffsetX * this.secondsPerPixel) * 1000),
        new Date((this.unixLimitFrom + downloadToOffsetX * this.secondsPerPixel) * 1000),
      );
    },
    /**
     * Сохранение границ для скачивания видео в допустимых пределах.
     *
     * @param {Date} downloadFrom
     * @param {Date} downloadTo
     */
    saveDownloadBordersMoving(downloadFrom, downloadTo) {
      if (+downloadFrom < +this.availableArchiveFrom) {
        downloadFrom = new Date(this.availableArchiveFrom);
      }
      if (+downloadTo > +this.availableArchiveTo) {
        downloadTo = new Date(this.availableArchiveTo);
      }

      const downloadDelta = downloadTo - downloadFrom;
      this.showWarnDownloadBorders = (downloadDelta < (this.minUnixDownloadDelta * 1000)) || (downloadDelta > (this.maxUnixDownloadDelta * 1000));
      if (+downloadTo < +downloadFrom) {
        return;
      }
      this.downloadTo = downloadTo;
      this.downloadFrom = downloadFrom;
      this.downloadFromOffsetX = Math.round((Math.trunc(downloadFrom / 1000) - this.unixLimitFrom) / this.secondsPerPixel);
      this.downloadToOffsetX = Math.round((Math.trunc(downloadTo / 1000) - this.unixLimitFrom) / this.secondsPerPixel);
      this.debouncedEmitDownloadBordersChange([downloadFrom, downloadTo]);
    },
    /**
     * Установка режима для отображения временной шкалы в области видимости текущего прогресса.
     * Отдельный метод необходим для точной установки режима в разных случаях.
     * Так иногда нужно установить видимость шкалы в области индикатора но только однократно.
     *
     * @param {Boolean} follow
     */
    setFollowTimeShift(follow = null) {
      if (follow === true) {
        this.followTimeShift = true;
      } else if (follow === false) {
        this.followTimeShift = false;
      } else {
        this.moveSeekBarToTimeShift();
      }
    },
    /**
     * Прокрутка колесика мыши на шкале с целью увеличить масштаб.
     *
     * @param {WheelEvent} wheelEvent
     */
    zoomSeekBar(wheelEvent) {
      this.saveSize(this.size + Math.sign(wheelEvent.deltaY), wheelEvent);
    },
    /**
     * Сохранение нового масштаба временной шкалы.
     *
     * При изменении масштаба меняется абсолютное значение ширины всей доступной временной шкалы.
     * Надо пересчитать смещение отображаемой области и прочие элементы на шкале.
     *
     * Если масштабирование происходит через колесико мыши, то происходит центрирование видимой области по мыши.
     * Если через внешнее изменение, то по центру видимой области.
     *
     * @param {Number} newSize
     * @param {WheelEvent} wheelEvent
     */
    saveSize(newSize, wheelEvent = null) {
      if ((newSize < 0) || (newSize >= TIMELINE_SIZES.length) || (this.size === newSize)) {
        return;
      }
      // Сначала фиксация изменений.
      const oldSize = this.size;
      this.size = newSize;
      // Потом все зависимые операции.
      if (wheelEvent) {
        // С колесиком видимая часть центруется по положению мыши.
        const seekBarTargetX = wheelEvent.pageX - this.$refs.seekBar.parentElement.getBoundingClientRect().left;
        this.saveSeekBarMoving((this.seekBarOffsetX - seekBarTargetX) * TIMELINE_SIZES[oldSize][0] / TIMELINE_SIZES[newSize][0] + seekBarTargetX);
        this.setFollowTimeShift(false);
      } else {
        // С внешними изменениями видимая часть будет в области видимости прогресса.
        this.setFollowTimeShift();
      }
      this.moveTimeShift();
      this.downloadFromOffsetX = (this.downloadFromOffsetX * TIMELINE_SIZES[oldSize][0] / TIMELINE_SIZES[newSize][0]);
      this.downloadToOffsetX = (this.downloadToOffsetX * TIMELINE_SIZES[oldSize][0] / TIMELINE_SIZES[newSize][0]);

      // Со сменой размера, все нарисованное на шкале теряет актуальность.
      this.advancedLeftBoundary = null;
      this.advancedRightBoundary = null;
      this.throttledUpdateLineFragments();

      /**
       * Событие для уведомления о принятии нового значения размера шкалы. Вернет порядковый номер в `TIMELINE_SIZES`.
       * @event timeline-size-change
       */
      this.$emit("timeline-size-change", newSize);
    },
  },
};
</script>

<style lang="scss">
@import "./scss/style.scss";
</style>
